import React, { useCallback, useEffect, useState } from "react";
import {
  FaCalendarAlt,
  FaChartLine,
  FaCreditCard,
  FaTimesCircle,
} from "react-icons/fa";
import { subscriptionService } from "../../../marketplace/services/subscriptionService";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { dateStyle: "medium" });
};

const formatPlanName = (plan) => {
  if (!plan) return "—";
  const map = { basic: "Basic", pro: "Pro", pro_plus: "Pro Plus", premium: "Premium" };
  return map[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
};

const StatCard = ({ icon, title, value }) => (
  <div className="flex items-center gap-2 rounded-xl bg-orange-50 p-2.5">
    <div className="rounded-lg bg-orange-100 p-1.5 text-base text-orange-500">{icon}</div>
    <div>
      <p className="text-xs text-gray-500">{title}</p>
      <p className="text-sm font-semibold text-gray-800">{value}</p>
    </div>
  </div>
);

const CompanySubscriptionBilling = ({ className = "" }) => {
  const [subscription, setSubscription] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const loadSubscription = useCallback(async (cacheBust = false) => {
    setBillingLoading(true);
    setBillingError(null);
    try {
      const res = await subscriptionService.getSubscription(cacheBust ? { cacheBust: true } : {});
      if (res?.success && res?.data?.subscription) {
        setSubscription(res.data.subscription);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      setBillingError(err.message || "Failed to load subscription");
      setSubscription(null);
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const handleCancelSubscription = async () => {
    if (cancelLoading || subscription?.cancelAtPeriodEnd) return;
    setShowCancelDialog(false);
    setCancelLoading(true);
    setBillingError(null);
    try {
      await subscriptionService.cancelSubscription();
      await loadSubscription(true);
    } catch (err) {
      setBillingError(err.message || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const isPaidActive = subscription?.planType && subscription.planType !== "basic" && subscription.isActive;

  return (
    <div className={className}>
      {billingLoading ? (
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 py-6 text-gray-500">
          <div className="h-5 w-5 animate-spin rounded-full border-t-2 border-b-2 border-orange-500" />
          <span>Loading subscription…</span>
        </div>
      ) : billingError ? (
        <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{billingError}</div>
      ) : subscription ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <StatCard
              icon={<FaCreditCard />}
              title="Active plan"
              value={formatPlanName(subscription.planType)}
            />
            <StatCard
              icon={<FaCalendarAlt />}
              title="Billing cycle"
              value={
                subscription.billingCycle
                  ? subscription.billingCycle.charAt(0).toUpperCase() + subscription.billingCycle.slice(1)
                  : "—"
              }
            />
            <StatCard
              icon={<FaChartLine />}
              title="Status"
              value={
                subscription.cancelAtPeriodEnd
                  ? "Cancelling at period end"
                  : subscription.isActive
                    ? "Active"
                    : subscription.status || "—"
              }
            />
            <StatCard
              icon={<FaCalendarAlt />}
              title="Access until"
              value={
                subscription.isActive && subscription.expiryDate
                  ? formatDate(subscription.expiryDate)
                  : "—"
              }
            />
          </div>

          {subscription.cancelAtPeriodEnd ? (
            <p className="inline-flex items-center gap-2 rounded-lg bg-amber-50 px-4 py-2 text-sm text-amber-700">
              <FaTimesCircle />
              Your subscription will end at the close of the current billing period. Access continues
              until {formatDate(subscription.expiryDate)}.
            </p>
          ) : null}

          {isPaidActive && !subscription.cancelAtPeriodEnd ? (
            <div className="border-t border-gray-100 pt-2">
              <div className="flex flex-col gap-4 rounded-xl bg-orange-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-black md:text-base">Cancel subscription</h3>
                  <p className="mt-0.5 text-[10px] tracking-tight text-gray-700 md:text-sm">
                    Keep access until your current billing period ends.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={cancelLoading}
                  className="shrink-0 rounded-lg border border-orange-500 bg-orange-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cancelLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Cancelling…
                    </span>
                  ) : (
                    "Cancel"
                  )}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-gray-500">No subscription data available.</p>
      )}

      {showCancelDialog ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !cancelLoading && setShowCancelDialog(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-labelledby="cancel-brand-subscription-title"
          >
            <h3 id="cancel-brand-subscription-title" className="mb-2 text-lg font-semibold text-black">
              Cancel subscription
            </h3>
            <p className="mb-6 text-sm leading-relaxed text-gray-700">
              Cancel at the end of the current billing period? You will keep access until{" "}
              {formatDate(subscription?.expiryDate)}.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelLoading}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-black disabled:opacity-60"
              >
                Go back
              </button>
              <button
                type="button"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {cancelLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Cancelling…
                  </span>
                ) : (
                  "Yes, cancel"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CompanySubscriptionBilling;
